"use client";

import { useEffect, useRef, type RefObject } from "react";

/**
 * True when the current page is actually on screen. A preloaded/parked webview
 * (Owlet warm-loads Felt pages at launch) reports either `visibilityState:
 * "hidden"` or a 0x0 viewport depending on how native parks it, so both gates
 * are needed. This is the shared "is the content visible" signal behind both
 * `content_visible` (PE-1534) and `screen_viewed`.
 */
export function isOnScreen(): boolean {
  return (
    window.innerWidth > 0 &&
    window.innerHeight > 0 &&
    document.visibilityState === "visible"
  );
}

interface UseOnVisibleOptions {
  /**
   * Element whose layout ticks drive the visibility re-check. Defaults to
   * `document.documentElement`. Pass a specific host ref (as `content_visible`
   * does) to re-check on that element's re-layout.
   */
  ref?: RefObject<Element | null>;
  /**
   * Re-run the visibility check when any of these change — e.g. a wizard step,
   * which changes without a re-layout, so the ResizeObserver alone wouldn't
   * re-fire.
   */
  watch?: readonly unknown[];
}

/**
 * Call `onVisible` whenever the observed target is on screen — the shared
 * visibility signal both `content_visible` and `screen_viewed` ride.
 *
 * Checks on mount, on every `ResizeObserver` tick (native re-layout, including
 * the 0x0→sized parking transition), on `visibilitychange` (hidden→visible),
 * and whenever a `watch` value changes. It only decides "visible now" and calls
 * back on every visible tick — the caller owns dedup (per route, per step, …).
 */
export function useOnVisible(
  onVisible: () => void,
  options: UseOnVisibleOptions = {}
): void {
  const { ref, watch = [] } = options;
  // Capture the latest callback without resubscribing the observers each render.
  const onVisibleRef = useRef(onVisible);
  onVisibleRef.current = onVisible;

  useEffect(() => {
    const check = () => {
      if (!isOnScreen()) return;
      onVisibleRef.current();
    };

    check();

    document.addEventListener("visibilitychange", check);
    const target = ref?.current ?? document.documentElement;
    const observer = new ResizeObserver(check);
    observer.observe(target);
    return () => {
      document.removeEventListener("visibilitychange", check);
      observer.disconnect();
    };
    // `watch` is spread so the check re-runs when a watched value (e.g. the
    // wizard step) changes, not just on layout/visibility events.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, ...watch]);
}
